import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sectionCapacity } from '@dosfilos/domain';
import { errorReason, errorText } from '../../../lib/error-text';
import { StackEntryOverrideField } from './StackEntryOverrideField';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from '@mui/material';
import {
  AssignmentOutlined,
  CheckCircleOutline,
  CancelOutlined,
  CloudUploadOutlined,
  SwapHoriz,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { SubmitAdmissionDocumentsDialog } from './SubmitAdmissionDocumentsDialog';
import { CreateAdmissionDialog } from './CreateAdmissionDialog';
import { AddRounded } from '@mui/icons-material';
import { doc as firestoreDoc, getDoc as firestoreGetDoc } from 'firebase/firestore';
import { formatDate } from '../../../lib/i18n-format';
import { callable } from '../../../lib/callable';

interface PersonAdmissionsTabProps {
  personId: string;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  submitted: 'info',
  under_review: 'warning',
  accepted: 'success',
  rejected: 'error',
  waitlisted: 'warning',
  withdrawn: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  submitted: 'Enviada',
  under_review: 'En revisión',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  waitlisted: 'En espera',
  withdrawn: 'Retirada',
};

const ACTIONABLE = new Set(['submitted', 'under_review', 'waitlisted']);

export function PersonAdmissionsTab({ personId }: PersonAdmissionsTabProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId, currentMembership } = useTenantStore();
  // Accepting and changing a program are gated on the server to admissions staff
  // (tenantAdmin, registrar). Other roles can open this tab (faculty and the
  // academic dean reach /people): they do not get buttons that would only fail.
  const canDecideAdmissions = (currentMembership?.roles ?? []).some(
    (r) => r === 'tenantAdmin' || r === 'registrar',
  );
  const [admissions, setAdmissions] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [personData, setPersonData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  } | null>(null);

  // New admission dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Confirm dialog
  const [confirmAction, setConfirmAction] = useState<{
    type: 'accept' | 'reject';
    admissionId: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [submitDocsDialog, setSubmitDocsDialog] = useState<{
    open: boolean;
    admissionId?: string;
  }>({ open: false });

  // ── Change Program dialog state ──────────────────────────────────────────
  const [changeProgramDialog, setChangeProgramDialog] = useState<{
    open: boolean;
    admissionId?: string;
    currentProgramId?: string;
  }>({ open: false });
  const [changeProgramStep, setChangeProgramStep] = useState<1 | 2 | 3>(1);
  const [availablePrograms, setAvailablePrograms] = useState<{ id: string; name: string }[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [enrollmentAction, setEnrollmentAction] = useState<'keep' | 'drop_all'>('keep');
  const [tuitionPlanDecision, setTuitionPlanDecision] = useState<'keep' | 'recreate'>('keep');
  const [notifyStudent, setNotifyStudent] = useState(false);
  const [availableSections, setAvailableSections] = useState<
    Array<{ id: string; name: string; capacity?: number; enrolled?: number }>
  >([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set());
  const [changeProgramLoading, setChangeProgramLoading] = useState(false);
  // Ladder entry rule refused the move / the acceptance: null = no override
  // asked; a string = the reason being typed for the retry.
  const [changeStackReason, setChangeStackReason] = useState<string | null>(null);
  const [acceptStackReason, setAcceptStackReason] = useState<string | null>(null);
  const [stackWarning, setStackWarning] = useState<string | null>(null);
  const [changeProgramError, setChangeProgramError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId || !personId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const q = query(
      collection(db, `tenants/${currentTenantId}/admissions`),
      where('personId', '==', personId),
    );

    const unsub = onSnapshot(
      q,
      async (snap) => {
        try {
          const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

          // Resolve program + term names + requirements
          const resolved = await Promise.all(
            raw.map(async (a: any) => {
              const [progSnap, termSnap] = await Promise.all([
                a.programId
                  ? getDoc(doc(db, `tenants/${currentTenantId}/programs`, a.programId))
                  : null,
                a.termId ? getDoc(doc(db, `tenants/${currentTenantId}/terms`, a.termId)) : null,
              ]);

              const progData = progSnap?.exists() ? progSnap.data() : null;

              return {
                ...a,
                programName: progData?.name || a.programId,
                termName: termSnap?.exists() ? termSnap.data()?.name : a.termId,
                documentRequirements: progData?.documentRequirements || [],
              };
            }),
          );

          setAdmissions(
            resolved.sort((a: any, b: any) => {
              return (
                (b.createdAt?.toDate?.()?.getTime() ?? 0) -
                (a.createdAt?.toDate?.()?.getTime() ?? 0)
              );
            }),
          );
        } catch (err) {
          console.error('[PersonAdmissionsTab] error:', err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('[PersonAdmissionsTab] Firestore error:', err);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [currentTenantId, personId]);

  // Fetch person data for the create dialog
  useEffect(() => {
    if (!currentTenantId || !personId) return;
    firestoreGetDoc(firestoreDoc(db, `tenants/${currentTenantId}/people`, personId)).then(
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setPersonData({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone,
          });
        }
      },
    );
  }, [currentTenantId, personId]);

  const handleAction = async () => {
    if (!confirmAction || !currentTenantId) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const fn = callable(confirmAction.type === 'accept' ? 'acceptAdmission' : 'rejectAdmission');
      const res = await fn({
        tenantId: currentTenantId,
        admissionId: confirmAction.admissionId,
        ...(confirmAction.type === 'accept' && acceptStackReason?.trim()
          ? { stackEntryOverride: { reason: acceptStackReason.trim() } }
          : {}),
      });
      const accepted = res.data as {
        stackEntryWarning?: { predecessorProgramName: string };
        programEnrollmentFailed?: boolean;
      };
      if (accepted?.stackEntryWarning) {
        setStackWarning(
          t('admissionDetail.stackEntry.warning', {
            program: accepted.stackEntryWarning.predecessorProgramName,
          }),
        );
      }
      if (accepted?.programEnrollmentFailed) {
        setStackWarning(t('admissionDetail.programEnrollmentFailed'));
      }
      setConfirmAction(null);
      setAcceptStackReason(null);
    } catch (err: unknown) {
      if (errorReason(err) === 'program_enrollment.predecessor_incomplete') {
        setAcceptStackReason((prev) => prev ?? '');
      }
      setActionError(errorText(err, 'Error al procesar'));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Load sections for selected program ────────────────────────────────
  const loadSectionsForProgram = async (programId: string, termId: string) => {
    if (!currentTenantId) return;
    try {
      const {
        collection: col,
        query: q,
        where: w,
        getDocs,
        getDoc,
        doc: firestoreDocRef,
      } = await import('firebase/firestore');
      const snap = await getDocs(
        q(
          col(db, `tenants/${currentTenantId}/sections`),
          w('programId', '==', programId),
          w('termId', '==', termId),
          w('status', '==', 'open'),
        ),
      );

      // Resolve course names for each section
      const sectionsWithNames = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data() as any;
          let courseName = data.courseId; // Fallback to courseId if no course found

          try {
            const courseSnap = await getDoc(
              firestoreDocRef(db, `tenants/${currentTenantId}/courses`, data.courseId),
            );
            if (courseSnap.exists()) {
              const courseData = courseSnap.data() as any;
              courseName = `${courseData.code || ''} - ${courseData.name || data.courseId}`.trim();
            }
          } catch {
            // Fallback if course lookup fails
          }

          return {
            id: d.id,
            name: courseName,
            capacity: sectionCapacity(data.maxEnrollment) ?? 0,
            enrolled: data.currentEnrollment || 0,
          };
        }),
      );

      setAvailableSections(sectionsWithNames);
      setSelectedSectionIds(new Set(sectionsWithNames.map((s) => s.id)));
    } catch (err) {
      console.error('[PersonAdmissionsTab] failed to load sections', err);
    }
  };

  // ── Open change program dialog: load active programs ─────────────────────
  const openChangeProgramDialog = async (admissionId: string, currentProgramId: string) => {
    setChangeProgramDialog({ open: true, admissionId, currentProgramId });
    setChangeStackReason(null);
    setChangeProgramStep(1);
    setSelectedProgramId('');
    setEnrollmentAction('keep');
    setTuitionPlanDecision('keep');
    setNotifyStudent(false);
    setAvailableSections([]);
    setSelectedSectionIds(new Set());
    setChangeProgramError(null);
    if (!currentTenantId) return;
    try {
      const { collection: col, query: q, where: w, getDocs } = await import('firebase/firestore');
      const snap = await getDocs(
        q(col(db, `tenants/${currentTenantId}/programs`), w('status', '==', 'active')),
      );
      const programs = snap.docs
        .map((d) => ({ id: d.id, name: (d.data() as any).name as string }))
        .filter((p) => p.id !== currentProgramId)
        .sort((a, b) => a.name.localeCompare(b.name, 'es'));
      setAvailablePrograms(programs);
    } catch (err) {
      console.error('[PersonAdmissionsTab] failed to load programs', err);
    }
  };

  const handleChangeProgram = async () => {
    if (!changeProgramDialog.admissionId || !selectedProgramId || !currentTenantId) return;
    setChangeProgramLoading(true);
    setChangeProgramError(null);
    try {
      const fn = callable('changeProgramWithEnrollmentTransition');
      const changed = await fn({
        tenantId: currentTenantId,
        admissionId: changeProgramDialog.admissionId,
        newProgramId: selectedProgramId,
        enrollmentTransition: {
          action: enrollmentAction,
        },
        sectionTransition: {
          action: selectedSectionIds.size > 0 ? 'add_selected' : 'none',
          selectedSectionIds: Array.from(selectedSectionIds),
        },
        tuitionPlanDecision,
        notifyStudent,
        ...(changeStackReason?.trim()
          ? { stackEntryOverride: { reason: changeStackReason.trim() } }
          : {}),
      });
      setChangeProgramDialog({ open: false });
      setChangeStackReason(null);
      // The program enrollment moves with the admission; if the new one could not
      // be created, say so (it is audited server-side).
      if (
        (changed.data as { programEnrollment?: { opened: boolean } })?.programEnrollment?.opened ===
        false
      ) {
        setStackWarning(t('admissionDetail.programEnrollmentFailed'));
      }
    } catch (err: unknown) {
      if (errorReason(err) === 'program_enrollment.predecessor_incomplete') {
        setChangeStackReason((prev) => prev ?? '');
      }
      setChangeProgramError(errorText(err, 'Error al cambiar el programa'));
    } finally {
      setChangeProgramLoading(false);
    }
  };

  const selectedProgramName = availablePrograms.find((p) => p.id === selectedProgramId)?.name ?? '';
  const currentProgramName =
    admissions.find((a) => a.programId === changeProgramDialog.currentProgramId)?.programName ??
    changeProgramDialog.currentProgramId ??
    '—';

  if (loading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (admissions.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <AssignmentOutlined sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
        <Typography variant="h6">Sin admisiones registradas</Typography>
        <Typography variant="body2">
          Las admisiones creadas para esta persona aparecerán aquí.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRounded />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ minWidth: 140 }}
        >
          Nueva Solicitud
        </Button>
      </Stack>
      {stackWarning && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStackWarning(null)}>
          {stackWarning}
        </Alert>
      )}
      <Stack spacing={2}>
        {admissions.map((a: any) => (
          <Card key={a.id} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {a.programName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Período: {a.termName} · Creada: {formatDate(a.createdAt) || '—'}
                  </Typography>
                  <Chip
                    label={STATUS_LABELS[a.status] ?? a.status}
                    size="small"
                    color={STATUS_COLORS[a.status] ?? 'default'}
                    variant="outlined"
                  />
                </Box>

                {ACTIONABLE.has(a.status) && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<CloudUploadOutlined />}
                      onClick={() => setSubmitDocsDialog({ open: true, admissionId: a.id })}
                    >
                      Subir Documentos
                    </Button>
                    {canDecideAdmissions && (
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        startIcon={<CheckCircleOutline />}
                        onClick={() => setConfirmAction({ type: 'accept', admissionId: a.id })}
                      >
                        {t('personAdmissions.accept')}
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<CancelOutlined />}
                      onClick={() => setConfirmAction({ type: 'reject', admissionId: a.id })}
                    >
                      Rechazar
                    </Button>
                  </Stack>
                )}
                {a.status === 'accepted' && canDecideAdmissions && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<SwapHoriz />}
                    onClick={() => openChangeProgramDialog(a.id, a.programId)}
                  >
                    {t('personAdmissions.changeProgram')}
                  </Button>
                )}
              </Stack>

              {a.notes && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {a.notes}
                  </Typography>
                </>
              )}

              {/* Document Checklist */}
              {a.documentRequirements && a.documentRequirements.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={1.5}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase' }}
                    >
                      Requisitos de Documentación
                    </Typography>
                    {a.documentRequirements.map((req: any, idx: number) => {
                      const uploadedDoc = a.documents?.find((d: any) => d.type === req.type);
                      const isVerified = uploadedDoc?.verified;

                      return (
                        <Stack
                          key={idx}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {req.type}{' '}
                              {req.required && (
                                <Box component="span" sx={{ color: 'error.main' }}>
                                  *
                                </Box>
                              )}
                            </Typography>
                            {req.description && (
                              <Typography variant="caption" color="text.secondary">
                                {req.description}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              isVerified ? 'Verificado' : uploadedDoc ? 'Recibido' : 'Pendiente'
                            }
                            color={isVerified ? 'success' : uploadedDoc ? 'info' : 'warning'}
                            sx={{ height: 20, fontSize: 10 }}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Confirm dialog */}
      <Dialog
        open={!!confirmAction}
        onClose={() => {
          setConfirmAction(null);
          setActionError(null);
          setAcceptStackReason(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={700}>
          {confirmAction?.type === 'accept' ? '✅ Aceptar admisión' : '❌ Rechazar admisión'}
        </DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          {acceptStackReason !== null && (
            <StackEntryOverrideField
              value={acceptStackReason}
              onChange={setAcceptStackReason}
              disabled={actionLoading}
            />
          )}
          <DialogContentText>
            {confirmAction?.type === 'accept'
              ? 'Esta persona pasará a ser Estudiante Activo.'
              : 'La admisión será marcada como rechazada.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setConfirmAction(null);
              setActionError(null);
              setAcceptStackReason(null);
            }}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={confirmAction?.type === 'accept' ? 'success' : 'error'}
            onClick={handleAction}
            disabled={actionLoading || (acceptStackReason !== null && !acceptStackReason.trim())}
          >
            {actionLoading ? 'Procesando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitAdmissionDocumentsDialog
        open={submitDocsDialog.open}
        admissionId={submitDocsDialog.admissionId}
        programRequirements={
          admissions.find((a) => a.id === submitDocsDialog.admissionId)?.documentRequirements
        }
        onClose={() => setSubmitDocsDialog({ open: false })}
      />

      <CreateAdmissionDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        personId={personId}
        initialData={personData || undefined}
      />

      {/* ── Change Program Dialog ── */}
      <Dialog
        open={changeProgramDialog.open}
        onClose={() => {
          if (!changeProgramLoading) setChangeProgramDialog({ open: false });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz color="secondary" />
          Cambiar Programa {changeProgramStep === 2 ? '· Revisión' : ''}{' '}
          {changeProgramStep === 3 ? '· Seleccionar Materias' : ''}
        </DialogTitle>
        <DialogContent>
          {changeProgramError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {changeProgramError}
            </Alert>
          )}
          {changeStackReason !== null && (
            <StackEntryOverrideField
              value={changeStackReason}
              onChange={setChangeStackReason}
              disabled={changeProgramLoading}
            />
          )}
          {changeProgramStep === 1 && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Selecciona el nuevo programa al que deseas reasignar al alumno. Este cambio guardará
                auditoría y conservará el historial de la admisión.
              </DialogContentText>
              <FormControl fullWidth size="small">
                <InputLabel id="change-program-label">Nuevo Programa</InputLabel>
                <Select
                  labelId="change-program-label"
                  value={selectedProgramId}
                  label="Nuevo Programa"
                  onChange={(e) => setSelectedProgramId(e.target.value)}
                  disabled={changeProgramLoading}
                >
                  {availablePrograms.length === 0 && (
                    <MenuItem disabled value="">
                      <em>No hay otros programas activos</em>
                    </MenuItem>
                  )}
                  {availablePrograms.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {changeProgramStep === 2 && (
            <Stack spacing={2}>
              <Alert severity="info">
                Revisa el impacto antes de confirmar. La transición cambiará el programa y
                matriculará automáticamente las secciones activas del nuevo programa en el período
                actual.
              </Alert>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Programa actual
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {currentProgramName}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Nuevo programa
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {selectedProgramName || '—'}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Matrícula actual
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {enrollmentAction === 'keep'
                    ? 'Se conservan las inscripciones actuales'
                    : 'Se darán de baja las inscripciones actuales'}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Plan de pago
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {tuitionPlanDecision === 'keep'
                    ? 'Se mantiene el plan actual'
                    : 'Se recrea el plan de pago'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Matrícula actual
                </Typography>
                <RadioGroup
                  value={enrollmentAction}
                  onChange={(e) => setEnrollmentAction(e.target.value as 'keep' | 'drop_all')}
                >
                  <FormControlLabel
                    value="keep"
                    control={<Radio size="small" />}
                    label="Conservar todas las inscripciones actuales"
                  />
                  <FormControlLabel
                    value="drop_all"
                    control={<Radio size="small" />}
                    label="Dar de baja todas las inscripciones actuales"
                  />
                </RadioGroup>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Plan de pago
                </Typography>
                <RadioGroup
                  value={tuitionPlanDecision}
                  onChange={(e) => setTuitionPlanDecision(e.target.value as 'keep' | 'recreate')}
                >
                  <FormControlLabel
                    value="keep"
                    control={<Radio size="small" />}
                    label="Mantener el plan actual"
                  />
                  <FormControlLabel
                    value="recreate"
                    control={<Radio size="small" />}
                    label="Recrear el plan de pago"
                  />
                </RadioGroup>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={notifyStudent}
                      onChange={(e) => setNotifyStudent(e.target.checked)}
                    />
                  }
                  label="Notificar al alumno sobre el cambio de programa"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Stack>
          )}

          {changeProgramStep === 3 && (
            <Stack spacing={2}>
              <Alert severity="success">
                Selecciona las materias en las que deseas inscribir al alumno del nuevo programa.
              </Alert>
              {availableSections.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  No hay secciones abiertas disponibles en el nuevo programa.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {availableSections.map((section) => (
                    <Box
                      key={section.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        bgcolor: selectedSectionIds.has(section.id)
                          ? 'action.selected'
                          : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: selectedSectionIds.has(section.id)
                            ? 'action.selected'
                            : 'action.hover',
                        },
                      }}
                      onClick={() => {
                        const updated = new Set(selectedSectionIds);
                        if (updated.has(section.id)) {
                          updated.delete(section.id);
                        } else {
                          updated.add(section.id);
                        }
                        setSelectedSectionIds(updated);
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '3px',
                          border: '2px solid',
                          borderColor: selectedSectionIds.has(section.id)
                            ? 'primary.main'
                            : 'divider',
                          bgcolor: selectedSectionIds.has(section.id)
                            ? 'primary.main'
                            : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {selectedSectionIds.has(section.id) && (
                          <Box component="span" sx={{ color: 'white', fontSize: '14px' }}>
                            ✓
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {section.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {section.capacity
                            ? t('personAdmissions.sectionEnrolled', {
                                enrolled: section.enrolled ?? 0,
                                capacity: section.capacity,
                              })
                            : t('personAdmissions.sectionEnrolledNoCap', {
                                enrolled: section.enrolled ?? 0,
                              })}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              if (changeProgramStep > 1) {
                setChangeProgramStep((prev) => (prev - 1) as 1 | 2 | 3);
              } else {
                setChangeProgramDialog({ open: false });
              }
            }}
            disabled={changeProgramLoading}
          >
            {changeProgramStep > 1 ? 'Atrás' : 'Cancelar'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
              if (changeProgramStep === 1) {
                const admission = admissions.find((a) => a.id === changeProgramDialog.admissionId);
                if (admission) {
                  await loadSectionsForProgram(selectedProgramId, admission.termId);
                }
                setChangeProgramStep(2);
                return;
              } else if (changeProgramStep === 2) {
                setChangeProgramStep(3);
                return;
              }
              handleChangeProgram();
            }}
            disabled={
              !selectedProgramId ||
              changeProgramLoading ||
              (changeStackReason !== null && !changeStackReason.trim())
            }
            startIcon={
              changeProgramLoading ? <CircularProgress size={16} color="inherit" /> : <SwapHoriz />
            }
          >
            {changeProgramLoading
              ? 'Cambiando...'
              : changeProgramStep === 1
                ? 'Continuar'
                : changeProgramStep === 2
                  ? 'Siguiente'
                  : 'Confirmar Cambio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
